import { appState, deviceAliases } from '../core/state';
import { esc } from '../ui/modal';
import { t } from '../ui/lang';
import { navigate } from '../core/router';
import { isCloudPhoneFarm, isDuoFarm, isAdspowerFarm } from '../core/farm-kind';
import { farmFlagHtml, farmBadgesHtml } from '../core/farm-row';
import { currentRole } from '../core/access';
import { createFarmFilterState, buildFarmFilter, applyFarmFilter, stampFarmFilterData, } from '../core/farm-list';
import { farmRowState } from '../farms/farms-view';
import { fetchDuoDevices } from '../farms/duo-devices';
import { fetchGeelarkDevices } from '../farms/geelark-devices';
import { fetchAdspowerDevices } from '../farms/adspower-devices';
import { addToWorkspace, removeFromWorkspace, clearWorkspace, inWorkspace, workspaceSize, getWorkspaceSnapshot, loadWorkspace, notifyWorkspaceChange, } from './workspace-store';
// Filter selection, module-level so it survives leaving the page and coming back —
// mirroring how the Farms list keeps its own.
const configFilter = createFarmFilterState();
function othersOf(claimedBy) {
    return (claimedBy ?? []).filter((login) => login !== appState.currentUserLogin);
}
/** Relay devices (host / aggregator / VMOS) come from the live snapshot — no request. */
function relayDevicesOf(farmId) {
    const out = [];
    for (const d of appState.allDevices.values()) {
        if (d.farmId !== farmId)
            continue;
        out.push({
            udid: d.udid,
            name: deviceAliases.get(d.udid) || d['ro.product.model'] || d.udid,
            online: d.state === 'device',
            present: true,
            others: othersOf(d.claimedBy),
        });
    }
    out.sort((a, b) => a.name.localeCompare(b.name));
    return out;
}
/** Devices the relay list never carries: cloud phones (duo / GeeLark) and AdsPower profiles,
 *  each fetched from its own farm-grid endpoint. */
async function polledDevicesOf(farm) {
    const { devices } = isDuoFarm(farm.farm_kind)
        ? await fetchDuoDevices(farm.id)
        : isAdspowerFarm(farm.farm_kind)
            ? await fetchAdspowerDevices(farm.id)
            : await fetchGeelarkDevices(farm.id);
    return devices.map((d) => ({
        udid: d.udid,
        name: d.name || d.udid,
        online: d.online,
        present: true,
        others: othersOf(d.claimedBy),
    }));
}
/**
 * Claimed devices of this farm the list above didn't produce — a revoked phone, a farm
 * whose agent is down, a cloud phone powered off. They must still be listed (ticked,
 * marked absent) or the operator would have no way left to release them.
 */
function appendMissing(farmId, devices) {
    const seen = new Set(devices.map((d) => d.udid));
    const missing = getWorkspaceSnapshot().claims
        .filter((c) => c.farmId === farmId && !seen.has(c.udid))
        .map((c) => ({ udid: c.udid, name: c.udid, online: false, present: false, others: [] }));
    return [...devices, ...missing];
}
function deviceRowHtml(farmId, d) {
    const claimed = inWorkspace(d.udid);
    const state = !d.present ? t('workspace.state_not_reported') : (d.online ? '' : t('workspace.state_offline'));
    const others = d.others.length > 0
        ? `<span class="wsp-others" title="${t('workspace.in_work_title', { logins: esc(d.others.join(', ')) })}">${t('workspace.in_work', { login: esc(d.others[0]) })}${d.others.length > 1 ? ` +${d.others.length - 1}` : ''}</span>`
        : '';
    return `
        <label class="wsp-device${d.present ? '' : ' wsp-device--absent'}">
            <input type="checkbox" data-udid="${esc(d.udid)}" data-farm-id="${farmId}" ${claimed ? 'checked' : ''} />
            <span class="wsp-device__name" title="${esc(d.udid)}">${esc(d.name)}</span>
            ${state ? `<span class="wsp-device__state">${state}</span>` : ''}
            ${others}
        </label>
    `;
}
/** Claims a farm holds. Works without its device list, so a collapsed row shows a tally. */
function selectedCount(farmId) {
    return getWorkspaceSnapshot().claims.filter((c) => c.farmId === farmId).length;
}
function slotsHtml() {
    const { limit } = getWorkspaceSnapshot();
    return limit > 0 ? t('workspace.slots_used', { used: workspaceSize(), limit }) : '';
}
export async function renderWorkspaceConfig() {
    const content = document.getElementById('content');
    const pageTitle = document.getElementById('page-title');
    pageTitle.textContent = t('workspace.config_title');
    content.innerHTML = `
        <div class="farm-devs-header">
            <button class="back-btn" id="back-btn">← ${t('workspace.title')}</button>
        </div>
        <p class="settings-hint" id="wsp-intro">
            ${t('workspace.config_intro')}
            <span class="wsp-limit" id="wsp-slots">${esc(slotsHtml())}</span>
        </p>
        <div class="farm-filter" id="wsp-filter"></div>
        <div class="wsp-list" id="wsp-list"><p class="settings-hint">${t('workspace.loading_farms')}</p></div>
        <p class="modal__err" id="wsp-err" style="display:none"></p>
    `;
    const listEl = document.getElementById('wsp-list');
    const errEl = document.getElementById('wsp-err');
    document.getElementById('back-btn').addEventListener('click', () => navigate({ page: 'workspace' }));
    // "Clear all" rides in the filter bar's trailing slot, the same way the Clicker page
    // puts its Settings button there, instead of needing a toolbar row of its own.
    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'tbl-btn wsp-clear';
    clearBtn.textContent = t('workspace.clear_all');
    // The board's own claims must be current before any checkbox is drawn.
    await loadWorkspace();
    if (!document.body.contains(listEl))
        return;
    // Devices resolved per farm on first expand, kept for the page's lifetime so
    // collapsing and re-expanding doesn't re-hit a rate-limited provider.
    const loaded = new Map();
    const refreshTallies = (farmId) => {
        if (farmId !== undefined) {
            const el = listEl.querySelector(`.wsp-farm[data-farm-id="${farmId}"] .wsp-farm__count`);
            if (el)
                el.textContent = String(selectedCount(farmId));
        }
        else {
            listEl.querySelectorAll('.wsp-farm').forEach((row) => {
                const id = parseInt(row.dataset['farmId'] ?? '', 10);
                const el = row.querySelector('.wsp-farm__count');
                if (el && Number.isInteger(id))
                    el.textContent = String(selectedCount(id));
            });
        }
        const slots = document.getElementById('wsp-slots');
        if (slots)
            slots.textContent = slotsHtml();
        clearBtn.disabled = workspaceSize() === 0;
    };
    clearBtn.disabled = workspaceSize() === 0;
    clearBtn.addEventListener('click', async () => {
        const n = workspaceSize();
        if (n === 0)
            return;
        const confirmText = n === 1
            ? t('workspace.clear_confirm_one')
            : t('workspace.clear_confirm_many', { count: n });
        if (!window.confirm(confirmText))
            return;
        clearBtn.disabled = true;
        errEl.style.display = 'none';
        const result = await clearWorkspace();
        if ('error' in result) {
            clearBtn.disabled = false;
            errEl.textContent = result.error;
            errEl.style.display = 'block';
            return;
        }
        // Untick in place rather than re-rendering, so every farm the operator had open
        // stays open and their scroll position holds.
        listEl.querySelectorAll('input[type="checkbox"]').forEach((cb) => { cb.checked = false; });
        refreshTallies();
        for (const udid of result.removed)
            notifyWorkspaceChange(udid, false);
    });
    const onToggle = async (cb) => {
        const udid = cb.dataset['udid'];
        const farmId = parseInt(cb.dataset['farmId'], 10);
        const wanted = cb.checked;
        cb.disabled = true;
        errEl.style.display = 'none';
        const err = wanted ? await addToWorkspace(farmId, udid) : await removeFromWorkspace(farmId, udid);
        cb.disabled = false;
        if (err) {
            cb.checked = !wanted; // server refused (e.g. the workspace limit) — revert
            errEl.textContent = err;
            errEl.style.display = 'block';
            return;
        }
        refreshTallies(farmId);
        notifyWorkspaceChange(udid, wanted);
    };
    const expand = async (farm, body, head) => {
        head.setAttribute('aria-expanded', 'true');
        body.hidden = false;
        if (!loaded.has(farm.id)) {
            body.innerHTML = `<p class="settings-hint">${t('workspace.loading_devices')}</p>`;
            const base = isCloudPhoneFarm(farm.farm_kind) || isAdspowerFarm(farm.farm_kind)
                ? await polledDevicesOf(farm)
                : relayDevicesOf(farm.id);
            if (!document.body.contains(body))
                return; // navigated away while fetching
            loaded.set(farm.id, appendMissing(farm.id, base));
        }
        const devices = loaded.get(farm.id);
        body.innerHTML = devices.length === 0
            ? `<p class="settings-hint">${t('workspace.farm_no_devices')}</p>`
            : devices.map((d) => deviceRowHtml(farm.id, d)).join('');
        body.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
            cb.addEventListener('change', () => { void onToggle(cb); });
        });
        refreshTallies(farm.id);
    };
    let farms = [];
    try {
        const res = await fetch('/api/farms');
        if (res.ok)
            farms = await res.json();
    }
    catch { /* fall through to the empty state */ }
    if (!document.body.contains(listEl))
        return;
    if (farms.length === 0) {
        listEl.innerHTML = `<p class="settings-hint">${t('workspace.no_farms')}</p>`;
        return;
    }
    farms.sort((a, b) => a.name.localeCompare(b.name));
    // Live online state resolved exactly as the Farms list resolves it, so the dot here
    // and the Online/Offline filter agree with that page rather than telling a second story.
    const onlineOf = new Map(farms.map((f) => [f.id, farmRowState(f).online]));
    listEl.innerHTML = farms.map((f) => `
        <div class="wsp-farm" data-farm-id="${f.id}">
            <button type="button" class="wsp-farm__head" aria-expanded="false">
                <span class="wsp-farm__caret" aria-hidden="true">›</span>
                <span class="status-dot ${onlineOf.get(f.id) ? 'status-dot--green' : 'status-dot--red'}"></span>
                ${farmFlagHtml(f.location)}
                <span class="wsp-farm__name">${esc(f.name)}</span>
                ${farmBadgesHtml(f.farm_kind, f.farm_type)}
                <span class="wsp-farm__tally"><span class="wsp-farm__count">${selectedCount(f.id)}</span> ${t('workspace.in_work_tally')}</span>
            </button>
            <div class="wsp-farm__devices" hidden></div>
        </div>
    `).join('');
    for (const f of farms) {
        const row = listEl.querySelector(`.wsp-farm[data-farm-id="${f.id}"]`);
        if (row)
            stampFarmFilterData(row, f, onlineOf.get(f.id) ?? false);
    }
    // The same bar the Farms list uses — search, Status, Type and (superadmin only) the
    // team chips — driven off those stamped attributes.
    const filterEl = document.getElementById('wsp-filter');
    if (filterEl) {
        void buildFarmFilter(filterEl, configFilter, farms, { showTeams: currentRole() === 'superadmin', trailing: clearBtn }, () => applyFarmFilter(listEl, configFilter, '.wsp-farm')).then(() => applyFarmFilter(listEl, configFilter, '.wsp-farm'));
    }
    for (const farm of farms) {
        const row = listEl.querySelector(`.wsp-farm[data-farm-id="${farm.id}"]`);
        const head = row.querySelector('.wsp-farm__head');
        const body = row.querySelector('.wsp-farm__devices');
        head.addEventListener('click', () => {
            if (head.getAttribute('aria-expanded') === 'true') {
                head.setAttribute('aria-expanded', 'false');
                body.hidden = true;
                return;
            }
            void expand(farm, body, head);
        });
        // A farm the operator already took devices from opens straight away: that is the
        // part of the list they came to check or change, and leaving it collapsed hides
        // the current selection behind a click per farm. Everything else stays closed,
        // so this fetches only the few farms actually in use.
        if (selectedCount(farm.id) > 0)
            void expand(farm, body, head);
    }
}
